import { useMemo } from 'react';

import { FIELD_CONFIG } from 'configs/columns';
import { STATES } from 'configs/states';
import {
  BooleanFilterConfigEntry,
  RangeFilterConfigEntry,
  MultiSelectFilterConfigEntry,
  isOption,
  SingleSelectFilterConfigEntry,
  ListInputFilterConfigEntry,
  DateRangeFilterConfigEntry,
} from 'ui-kit';
import { isNotNull } from 'features/deals/DealStages/EventActionCards/utils';
import { invert, cloneDeep, map } from 'lodash';
import _ from 'lodash';
import { isArrayOf, isEnumMember, isType } from 'utils/typeUtils';

import { useQuery } from '@apollo/client';

import { AssetClass, FilterableField, FilterOperator } from '__generated__/globalTypes';
import { GetValidTerms, GetValidTermsVariables } from 'query/__generated__/getValidTerms';
import { GET_VALID_TERMS } from 'query/getValidTerms';

import { useFeatureFlagVersion } from '../../../../context/FeatureFlagContext';

const DATE_RANGE_MIN_SECONDS = Math.floor(new Date('2000-01-01T00:00:00Z').getTime() / 1000);
const DATE_RANGE_MAX_SECONDS = Math.floor(new Date('2100-01-01T00:00:00Z').getTime() / 1000);

const filterFieldToAbbr = {
  [FilterableField.account_id]: 'acc',
  [FilterableField.age_months]: 'age',
  [FilterableField.auto_original_value_cents]: 'aov',
  [FilterableField.borrower_credit_score]: 'bcs',
  [FilterableField.current_balance_cents]: 'cba',
  [FilterableField.dti_bps]: 'dti',
  [FilterableField.interest_rate_bps]: 'ir',
  [FilterableField.last_loan_tape_name]: 'ltn',
  [FilterableField.ltv_bps]: 'ltv',
  [FilterableField.occupancy]: 'occ',
  [FilterableField.original_balance_cents]: 'oba',
  [FilterableField.product]: 'p',
  [FilterableField.purpose]: 'pur',
  [FilterableField.remaining_loan_terms_months]: 'rlt',
  [FilterableField.cra]: 'cra',
  [FilterableField.state]: 'st',
  [FilterableField.credit_line_amount_cents]: 'cla',
  [FilterableField.lien_position]: 'lp',
  [FilterableField.combined_ltv_bps]: 'cltv',
  [FilterableField.draw_period_months]: 'dpm',
  [FilterableField.dealer_reserve_cents]: 'drc',
  [FilterableField.dealer_reserve_bps]: 'drb',
  [FilterableField.interest_only]: 'io',
  [FilterableField.periodic_cap_rate_bps]: 'pcr',
  [FilterableField.lifetime_cap_rate_bps]: 'lcr',
  [FilterableField.lifetime_floor_rate_bps]: 'lfr',
  [FilterableField.io_period_months]: 'iop',
  [FilterableField.amortization_term_months]: 'amt',
  [FilterableField.balloon_term_months]: 'blt',
  [FilterableField.irap_months]: 'irp',
  [FilterableField.rap_months]: 'rap',
  [FilterableField.maturity_date_seconds]: 'mds',
  [FilterableField.next_payment_date_seconds]: 'npd',
  [FilterableField.origination_date_seconds]: 'ods',
  [FilterableField.first_payment_date_seconds]: 'fpd',
  [FilterableField.first_rate_change_date_seconds]: 'frc',
};

const abbrToFilterField = invert(filterFieldToAbbr) as Record<string, PortfolioFilterField>;

const portfolioHomeFiltersConfig = {
  [FilterableField.account_id]: {
    type: 'list-input',
    displayName: FIELD_CONFIG.account_id.string,
  },
  [FilterableField.age_months]: {
    type: 'range',
    displayName: FIELD_CONFIG.age_months.string,
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.cra]: {
    type: 'boolean',
    displayName: FIELD_CONFIG.cra.string,
    falseyLabel: 'Without CRA',
    truthyLabel: 'With CRA',
  },
  [FilterableField.state]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.state.string,
    filterableFieldName: FilterableField.state,
    filterOptions: map(STATES, (_value, key) => ({ value: key, label: key })),
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.borrower_credit_score]: {
    type: 'range',
    displayName: FIELD_CONFIG.borrower_credit_score.string,
    min: 300,
    max: 850,
    integer: true,
  },
  [FilterableField.current_balance_cents]: {
    type: 'range',
    displayName: FIELD_CONFIG.current_balance_cents.string,
    min: 1,
    max: 200000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.dti_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.dti_bps.string,
    min: 500,
    max: 15000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${Math.round(bps / 100)}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.interest_rate_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.interest_rate_bps.string,
    min: 50,
    max: 900,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.ltv_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.ltv_bps.string,
    min: 500,
    max: 15000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${Math.round(bps / 100)}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.occupancy]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.occupancy.string,
    filterableFieldName: FilterableField.occupancy,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.last_loan_tape_name]: {
    type: 'select-multi',
    displayName: 'Loan Tape',
    filterableFieldName: FilterableField.last_loan_tape_name,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.product]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.product.string,
    filterableFieldName: FilterableField.product,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.purpose]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.purpose.string,
    filterableFieldName: FilterableField.purpose,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.original_balance_cents]: {
    type: 'range',
    displayName: FIELD_CONFIG.original_balance_cents.string,
    min: 1,
    max: 200000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.remaining_loan_terms_months]: {
    type: 'range',
    displayName: FIELD_CONFIG.remaining_loan_terms.string,
    min: 1,
    max: 360,
  },
  [FilterableField.interest_only]: {
    type: 'boolean',
    displayName: 'Interest Only',
    falseyLabel: 'Amortizing',
    truthyLabel: 'Interest Only',
  },
  [FilterableField.periodic_cap_rate_bps]: {
    type: 'range',
    displayName: 'Periodic Cap',
    min: 0,
    // 2000, matching lifetime_cap_rate_bps below: cashflows defaults an
    // absent value to 2000bps, so a max of 500 silently dropped every loan
    // with no periodic cap the moment this slider's upper handle was
    // actually dragged (not just added and left at its default range).
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.lifetime_cap_rate_bps]: {
    type: 'range',
    displayName: 'Lifetime Cap',
    min: 0,
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.lifetime_floor_rate_bps]: {
    type: 'range',
    displayName: 'Lifetime Floor',
    min: 0,
    // 2000, same reasoning as periodic_cap_rate_bps above: cashflows
    // defaults an absent value to 2000bps, so a max of 1000 silently
    // dropped every loan with no lifetime floor once this slider's upper
    // handle was actually dragged.
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.io_period_months]: {
    type: 'range',
    displayName: 'I/O Term (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.amortization_term_months]: {
    type: 'range',
    displayName: 'Amortization Term (Months)',
    min: 0,
    max: 600,
    integer: true,
  },
  [FilterableField.balloon_term_months]: {
    type: 'range',
    displayName: 'Balloon Term (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.irap_months]: {
    type: 'range',
    displayName: 'IRAP (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.rap_months]: {
    type: 'range',
    displayName: 'RAP (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.maturity_date_seconds]: {
    type: 'date-range',
    displayName: 'Maturity Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.next_payment_date_seconds]: {
    type: 'date-range',
    displayName: 'Next Payment Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.origination_date_seconds]: {
    type: 'date-range',
    displayName: 'Origination Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.first_payment_date_seconds]: {
    type: 'date-range',
    displayName: 'First Payment Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.first_rate_change_date_seconds]: {
    type: 'date-range',
    displayName: 'First Rate Change Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
} satisfies {
  [key in FilterableField]?:
    | RangeFilterConfigEntry
    | BooleanFilterConfigEntry
    | MultiSelectFilterConfigEntry<{ value: string; label: string }>
    | SingleSelectFilterConfigEntry<{ value: string; label: string }>
    | ListInputFilterConfigEntry
    | DateRangeFilterConfigEntry;
};

const portfolioHomeFiltersConfigV2 = {
  [FilterableField.account_id]: {
    type: 'list-input',
    displayName: FIELD_CONFIG.account_id.string,
  },
  [FilterableField.age_months]: {
    type: 'range',
    displayName: FIELD_CONFIG.age_months.string,
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.cra]: {
    type: 'boolean',
    displayName: FIELD_CONFIG.cra.string,
    falseyLabel: 'Without CRA',
    truthyLabel: 'With CRA',
  },
  [FilterableField.state]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.state.string,
    filterableFieldName: FilterableField.state,
    filterOptions: map(STATES, (_value, key) => ({ value: key, label: key })),
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.borrower_credit_score]: {
    type: 'range',
    displayName: FIELD_CONFIG.borrower_credit_score.string,
    min: 300,
    max: 850,
    integer: true,
  },
  [FilterableField.current_balance_cents]: {
    type: 'range',
    displayName: FIELD_CONFIG.current_balance_cents.string,
    min: 1,
    max: 200000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.dti_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.dti_bps.string,
    min: 500,
    max: 15000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${Math.round(bps / 100)}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.interest_rate_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.interest_rate_bps.string,
    min: 50,
    max: 900,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.ltv_bps]: {
    type: 'range',
    displayName: FIELD_CONFIG.ltv_bps.string,
    min: 500,
    max: 15000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${Math.round(bps / 100)}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.occupancy]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.occupancy.string,
    filterableFieldName: FilterableField.occupancy,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.last_loan_tape_name]: {
    type: 'select-multi',
    displayName: 'Loan Tape',
    filterableFieldName: FilterableField.last_loan_tape_name,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.product]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.product.string,
    filterableFieldName: FilterableField.product,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.purpose]: {
    type: 'select-multi',
    displayName: FIELD_CONFIG.purpose.string,
    filterableFieldName: FilterableField.purpose,
    filterOptions: [] as { value: string; label: string }[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (options) =>
      isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
  },
  [FilterableField.original_balance_cents]: {
    type: 'range',
    displayName: FIELD_CONFIG.original_balance_cents.string,
    min: 1,
    max: 200000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.remaining_loan_terms_months]: {
    type: 'range',
    displayName: FIELD_CONFIG.remaining_loan_terms.string,
    min: 1,
    max: 360,
  },
  [FilterableField.interest_only]: {
    type: 'boolean',
    displayName: 'Interest Only',
    falseyLabel: 'Amortizing',
    truthyLabel: 'Interest Only',
  },
  [FilterableField.periodic_cap_rate_bps]: {
    type: 'range',
    displayName: 'Periodic Cap',
    min: 0,
    // 2000, matching lifetime_cap_rate_bps below: cashflows defaults an
    // absent value to 2000bps, so a max of 500 silently dropped every loan
    // with no periodic cap the moment this slider's upper handle was
    // actually dragged (not just added and left at its default range).
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.lifetime_cap_rate_bps]: {
    type: 'range',
    displayName: 'Lifetime Cap',
    min: 0,
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.lifetime_floor_rate_bps]: {
    type: 'range',
    displayName: 'Lifetime Floor',
    min: 0,
    // 2000, same reasoning as periodic_cap_rate_bps above: cashflows
    // defaults an absent value to 2000bps, so a max of 1000 silently
    // dropped every loan with no lifetime floor once this slider's upper
    // handle was actually dragged.
    max: 2000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${bps / 100}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.io_period_months]: {
    type: 'range',
    displayName: 'I/O Term (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.amortization_term_months]: {
    type: 'range',
    displayName: 'Amortization Term (Months)',
    min: 0,
    max: 600,
    integer: true,
  },
  [FilterableField.balloon_term_months]: {
    type: 'range',
    displayName: 'Balloon Term (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.irap_months]: {
    type: 'range',
    displayName: 'IRAP (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.rap_months]: {
    type: 'range',
    displayName: 'RAP (Months)',
    min: 0,
    max: 360,
    integer: true,
  },
  [FilterableField.maturity_date_seconds]: {
    type: 'date-range',
    displayName: 'Maturity Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.next_payment_date_seconds]: {
    type: 'date-range',
    displayName: 'Next Payment Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.origination_date_seconds]: {
    type: 'date-range',
    displayName: 'Origination Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.first_payment_date_seconds]: {
    type: 'date-range',
    displayName: 'First Payment Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
  [FilterableField.first_rate_change_date_seconds]: {
    type: 'date-range',
    displayName: 'First Rate Change Date',
    min: DATE_RANGE_MIN_SECONDS,
    max: DATE_RANGE_MAX_SECONDS,
  },
} satisfies {
  [key in FilterableField]?:
    | RangeFilterConfigEntry
    | BooleanFilterConfigEntry
    | MultiSelectFilterConfigEntry<{ value: string; label: string }>
    | SingleSelectFilterConfigEntry<{ value: string; label: string }>
    | ListInputFilterConfigEntry
    | DateRangeFilterConfigEntry;
};

const portfolioCarsFiltersConfig = {
  ...cloneDeep(portfolioHomeFiltersConfig),
  [FilterableField.auto_original_value_cents]: {
    type: 'range' as const,
    displayName: FIELD_CONFIG.auto_original_value_cents.string,
    min: 1,
    max: 100000,
    prepare: (dollars: number) => dollars * 100,
  },
};

portfolioCarsFiltersConfig[FilterableField.ltv_bps].max = 15000;
portfolioCarsFiltersConfig[FilterableField.age_months].max = 60;
portfolioCarsFiltersConfig[FilterableField.current_balance_cents].max = 100000;
portfolioCarsFiltersConfig[FilterableField.interest_rate_bps].max = 900;
portfolioCarsFiltersConfig[FilterableField.original_balance_cents].max = 100000;
portfolioCarsFiltersConfig[FilterableField.remaining_loan_terms_months].max = 84;

const portfolioUnsecuredPersonalFiltersConfig = {
  ...cloneDeep(portfolioCarsFiltersConfig),
};

if (FilterableField.state in portfolioUnsecuredPersonalFiltersConfig) {
  // @ts-ignore - The 'delete' operator is used to remove a non-optional property
  delete portfolioUnsecuredPersonalFiltersConfig[FilterableField.state];
}

// HELOC filter config. Starts from the HOME filter set (covers shared
// consumer-secured fields: state, credit score, current balance, dti,
// interest rate, etc.) and layers HELOC-specific range filters on top.
// CRE reuses HOME's config; HELOC needs its own because the HELOC-specific
// fields (lien_position, combined_ltv, credit_line, draw_period) are only
// meaningful for HELOC and would be noise on the HOME/CRE sidebar.
const portfolioHelocFiltersConfig = {
  ...cloneDeep(portfolioHomeFiltersConfig),
  [FilterableField.credit_line_amount_cents]: {
    type: 'range',
    displayName: 'Credit Line',
    min: 1,
    max: 200000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.lien_position]: {
    type: 'range',
    displayName: 'Lien Position',
    min: 1,
    max: 10,
    integer: true,
  },
  [FilterableField.combined_ltv_bps]: {
    type: 'range',
    displayName: 'CLTV',
    min: 500,
    max: 15000,
    inputFormat: (pctStr: string) => parseFloat(pctStr) * 100,
    displayFormat: (bps: number) => `${Math.round(bps / 100)}`,
    integer: true,
    percentage: true,
  },
  [FilterableField.draw_period_months]: {
    type: 'range',
    displayName: 'Draw Period (Months)',
    min: 1,
    max: 360,
    integer: true,
  },
};

// Marine filter config (ENG-2961). Marine is secured-consumer, so it starts
// from the HOME filter set and layers the indirect-lending dealer-reserve
// ranges on top. (charter is a boolean capture flag — omitted from the
// portfolio sidebar for v1; still filterable via the datatable/cashflows.)
const portfolioMarineFiltersConfig = {
  ...cloneDeep(portfolioHomeFiltersConfig),
  [FilterableField.dealer_reserve_cents]: {
    type: 'range',
    displayName: 'Dealer Reserve',
    min: 1,
    max: 100000000,
    integer: true,
    prepare: (dollars: number) => dollars * 100,
  },
  [FilterableField.dealer_reserve_bps]: {
    type: 'range',
    displayName: 'Dealer Reserve (bps)',
    min: 0,
    max: 10000,
    integer: true,
  },
};

// ARM rate-mechanics and mortgage amortization-structure fields. These only make sense
// for rate-adjustable, amortizing products (HOME/CRE, and HELOC which is variable-rate).
// cloneDeep(portfolioHomeFiltersConfig) would otherwise leak them onto the fixed-rate
// consumer sidebars (auto/unsecured/marine), where they are pure noise. General date
// fields (maturity, origination, next/first payment) are intentionally left in place --
// they apply across asset classes.
const armRateAndStructureFields = [
  FilterableField.periodic_cap_rate_bps,
  FilterableField.lifetime_cap_rate_bps,
  FilterableField.lifetime_floor_rate_bps,
  FilterableField.first_rate_change_date_seconds,
  FilterableField.io_period_months,
  FilterableField.irap_months,
  FilterableField.rap_months,
  FilterableField.balloon_term_months,
  FilterableField.amortization_term_months,
  FilterableField.interest_only,
];

function stripFields<T extends object>(config: T, fields: FilterableField[]): T {
  for (const field of fields) {
    if (field in config) {
      // @ts-ignore - deleting a non-optional key from the shared config shape
      delete config[field];
    }
  }
  return config;
}

// Fixed-rate consumer asset classes: drop the ARM/amortization block inherited from HOME.
stripFields(portfolioCarsFiltersConfig, armRateAndStructureFields);
stripFields(portfolioUnsecuredPersonalFiltersConfig, armRateAndStructureFields);
stripFields(portfolioMarineFiltersConfig, armRateAndStructureFields);

function getPortfolioFiltersConfig(assetClass: AssetClass, version = 'v1') {
  switch (assetClass) {
    case AssetClass.HOME:
    case AssetClass.CRE:
      return version === 'v1' ? portfolioHomeFiltersConfig : portfolioHomeFiltersConfigV2;
    case AssetClass.HELOC:
      return portfolioHelocFiltersConfig;
    case AssetClass.MARINE:
      return portfolioMarineFiltersConfig;
    case AssetClass.AUTO:
      return portfolioCarsFiltersConfig;
    case AssetClass.UNSECPERSONAL:
      return portfolioUnsecuredPersonalFiltersConfig;
    default:
      throw new Error(`No filters config for asset class ${assetClass}`);
  }
}

type HasFilterableField = { filterableFieldName: FilterableField };
const hasFilterableField = isType<HasFilterableField>({
  filterableFieldName: isEnumMember(FilterableField),
});

function useFilterConfig(
  assetClass: AssetClass | null,
  input: Omit<GetValidTermsVariables['input'], 'fieldNames'>,
) {
  const version = useFeatureFlagVersion('pool-filters');

  const filterConfig = useMemo(() => {
    const cfg =
      assetClass &&
      (getPortfolioFiltersConfig(
        assetClass,
        version || 'v1',
      ) as typeof portfolioHomeFiltersConfigV2);
    return cfg;
  }, [assetClass, version]);

  const termFieldNames = useMemo(
    () =>
      filterConfig
        ? _.values(filterConfig)
            .filter((filter) => filter.type === 'select-multi')
            .map((filter) => (hasFilterableField(filter) ? filter.filterableFieldName : null))
            .filter(isNotNull)
        : [],
    [filterConfig],
  );

  const { data: validTermsData } = useQuery<GetValidTerms, GetValidTermsVariables>(
    GET_VALID_TERMS,
    {
      variables: {
        input: {
          ...input,
          filters: [
            ...(input.filters != null ? input.filters : []),
            {
              field_name: FilterableField.asset_class,
              operator: FilterOperator.IS,
              operand: assetClass,
            },
          ],
          fieldNames: termFieldNames,
        },
      },
      skip: !assetClass,
    },
  );
  const portfolioFiltersConfig = useMemo(() => {
    if (!filterConfig || !validTermsData) {
      return filterConfig;
    }
    const filtersConfig = { ...filterConfig };

    Object.values(filtersConfig).forEach((filter) => {
      if (filter.type === 'select-multi') {
        filter.filterOptions =
          validTermsData?.getValidTerms
            .find((validTerms) => validTerms.fieldName === filter.filterableFieldName)
            ?.terms.map((term) => ({
              label: term,
              value: term,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || filter.filterOptions;
      }
    });
    return filtersConfig;
  }, [validTermsData, filterConfig]);

  return portfolioFiltersConfig;
}

type PortfolioFilterConfig = ReturnType<typeof getPortfolioFiltersConfig>;
type PortfolioFilterField = keyof PortfolioFilterConfig;

export type { PortfolioFilterConfig, PortfolioFilterField };

export {
  abbrToFilterField,
  filterFieldToAbbr,
  portfolioHomeFiltersConfig,
  getPortfolioFiltersConfig,
  useFilterConfig,
};
